import { useState, useEffect, ChangeEvent } from 'react';
import { Save, Loader2, CheckCircle, AlertCircle, RefreshCw, Plus, Trash2, ListChecks, Megaphone, MessageCircle, Store, Image as ImageIcon } from 'lucide-react';
import { adminApi, AppConfig } from '../services/adminApi';

export default function Settings() {
  const [config, setConfig] = useState<AppConfig>({
    dataVersion: '',
    consultationServices: [],
    zaloGroupLink: '',
    shopeeServices: [],
    consultationImage: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingImg, setUploadingImg] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 3500);
  };

  const load = async () => {
    try {
      const res = await adminApi.getConfig();
      if (res.success && res.data) setConfig(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await adminApi.updateConfig({
        consultationServices: config.consultationServices.map((s) => s.trim()).filter(Boolean),
        zaloGroupLink: config.zaloGroupLink.trim(),
        shopeeServices: config.shopeeServices
          .map((s) => ({ label: s.label.trim(), url: s.url.trim() }))
          .filter((s) => s.label && s.url),
        consultationImage: config.consultationImage,
      });
      if (res.success && res.data) setConfig(res.data);
      showMessage('success', 'Đã lưu cài đặt');
    } catch (err) {
      showMessage('error', (err as Error).message || 'Lỗi khi lưu');
    } finally {
      setSaving(false);
    }
  };

  const handleUploadImage = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setUploadingImg(true);
      const url = await adminApi.uploadEbookImage(file);
      setConfig((c) => ({ ...c, consultationImage: url }));
      showMessage('success', 'Đã tải ảnh lên — nhớ bấm "Lưu cài đặt"');
    } catch (err) {
      showMessage('error', (err as Error).message || 'Upload ảnh thất bại');
    } finally {
      setUploadingImg(false);
    }
  };

  const handlePublish = async () => {
    setPublishing(true);
    try {
      const res = await adminApi.bumpDataVersion();
      if (res.success && res.data) setConfig((c) => ({ ...c, dataVersion: res.data!.dataVersion }));
      showMessage('success', 'Đã công bố — người dùng sẽ nhận dữ liệu mới ở lần tải kế tiếp');
    } catch (err) {
      showMessage('error', (err as Error).message || 'Lỗi khi công bố');
    } finally {
      setPublishing(false);
    }
  };

  if (loading) {
    return (
      <div className="center-screen">
        <Loader2 size={32} className="spin" style={{ color: 'var(--red-600)' }} />
      </div>
    );
  }

  return (
    <div className="admin-container">
      {message && (
        <div className={`toast ${message.type}`}>
          {message.type === 'success' ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
          {message.text}
        </div>
      )}

      <div className="page-head">
        <div>
          <h2 className="page-title">Cài Đặt</h2>
          <p className="page-subtitle">Công bố thay đổi · Nhóm hỗ trợ · Dịch vụ Shopee · Dịch vụ tư vấn</p>
        </div>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? <Loader2 size={18} className="spin" /> : <Save size={18} />} Lưu cài đặt
        </button>
      </div>

      <div className="stack" style={{ gap: 20 }}>
        {/* Công bố thay đổi */}
        <div className="card card-pad row-between" style={{ borderColor: '#bbf7d0' }}>
          <div className="row" style={{ alignItems: 'flex-start' }}>
            <div
              style={{
                width: 40, height: 40, borderRadius: 10, background: '#dcfce7',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}
            >
              <Megaphone size={20} style={{ color: '#16a34a' }} />
            </div>
            <div>
              <h3 style={{ fontWeight: 600, color: 'var(--slate-800)' }}>Công bố thay đổi (phiên bản dữ liệu)</h3>
              <p className="page-subtitle" style={{ maxWidth: 520 }}>
                Sau khi sửa phí / ngành hàng / ebook / footer, bấm để người dùng nhận dữ liệu mới ngay (không chờ cache hết hạn).
                Phiên bản hiện tại:{' '}
                <code style={{ background: 'var(--slate-100)', padding: '2px 6px', borderRadius: 4 }}>
                  {config.dataVersion || '—'}
                </code>
              </p>
            </div>
          </div>
          <button className="btn btn-primary" onClick={handlePublish} disabled={publishing}>
            {publishing ? <Loader2 size={18} className="spin" /> : <RefreshCw size={18} />} Công bố
          </button>
        </div>

        {/* Ảnh popup tư vấn */}
        <div className="card">
          <div className="card-head">
            <h3><ImageIcon size={18} style={{ color: '#2563eb' }} /> Ảnh popup tư vấn</h3>
          </div>
          <div className="card-pad">
            <p className="page-subtitle" style={{ marginBottom: 12 }}>
              Ảnh hiển thị bên trái popup tư vấn. Bỏ trống sẽ dùng ảnh mặc định.
            </p>
            {config.consultationImage ? (
              <img
                src={config.consultationImage}
                alt="popup tư vấn"
                style={{ maxWidth: 280, width: '100%', borderRadius: 10, border: '1px solid var(--slate-200)', display: 'block', marginBottom: 12 }}
              />
            ) : (
              <p className="muted" style={{ fontStyle: 'italic', marginBottom: 12 }}>Chưa có ảnh — đang dùng ảnh mặc định.</p>
            )}
            <div className="row">
              <label className="btn btn-secondary" style={{ cursor: uploadingImg ? 'not-allowed' : 'pointer' }}>
                {uploadingImg ? <Loader2 size={16} className="spin" /> : <ImageIcon size={16} />}
                {uploadingImg ? 'Đang tải...' : 'Chọn ảnh'}
                <input type="file" accept="image/*" style={{ display: 'none' }} disabled={uploadingImg} onChange={handleUploadImage} />
              </label>
              {config.consultationImage && (
                <button className="btn-icon danger" title="Xoá ảnh" onClick={() => setConfig({ ...config, consultationImage: '' })}>
                  <Trash2 size={16} />
                </button>
              )}
            </div>
            <p className="muted" style={{ fontSize: '0.78rem', marginTop: 10 }}>Tải ảnh xong nhớ bấm "Lưu cài đặt" ở trên.</p>
          </div>
        </div>

        {/* Nhóm hỗ trợ Zalo */}
        <div className="card">
          <div className="card-head">
            <h3><MessageCircle size={18} style={{ color: '#2563eb' }} /> Nhóm hỗ trợ miễn phí (link Zalo)</h3>
          </div>
          <div className="card-pad">
            <p className="page-subtitle" style={{ marginBottom: 10 }}>
              Link mở khi bấm nút "Nhóm hỗ trợ miễn phí" ở header trang public.
            </p>
            <input
              className="input"
              value={config.zaloGroupLink}
              placeholder="https://zalo.me/g/..."
              onChange={(e) => setConfig({ ...config, zaloGroupLink: e.target.value })}
            />
          </div>
        </div>

        {/* Menu Dịch vụ Shopee */}
        <div className="card">
          <div className="card-head">
            <h3><Store size={18} style={{ color: '#2563eb' }} /> Menu "Dịch vụ Shopee" (header)</h3>
          </div>
          <div className="card-pad stack" style={{ gap: 10 }}>
            <p className="page-subtitle">
              Danh sách hiện trong dropdown "Dịch vụ Shopee" ở header. Mỗi mục gồm tên hiển thị + link.
            </p>
            {config.shopeeServices.map((svc, i) => (
              <div className="row" key={i} style={{ alignItems: 'center' }}>
                <input
                  className="input"
                  style={{ flex: '0 0 38%' }}
                  value={svc.label}
                  placeholder="Tên hiển thị (VD: VẬN HÀNH GIAN HÀNG)"
                  onChange={(e) =>
                    setConfig({
                      ...config,
                      shopeeServices: config.shopeeServices.map((s, idx) => (idx === i ? { ...s, label: e.target.value } : s)),
                    })
                  }
                />
                <input
                  className="input"
                  value={svc.url}
                  placeholder="https://..."
                  onChange={(e) =>
                    setConfig({
                      ...config,
                      shopeeServices: config.shopeeServices.map((s, idx) => (idx === i ? { ...s, url: e.target.value } : s)),
                    })
                  }
                />
                <button
                  className="btn-icon danger"
                  onClick={() => setConfig({ ...config, shopeeServices: config.shopeeServices.filter((_, idx) => idx !== i) })}
                  title="Xoá"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
            {config.shopeeServices.length === 0 && (
              <p className="muted" style={{ fontStyle: 'italic' }}>Chưa có mục nào.</p>
            )}
            <button
              className="add-link"
              style={{ alignSelf: 'flex-start' }}
              onClick={() => setConfig({ ...config, shopeeServices: [...config.shopeeServices, { label: '', url: '' }] })}
            >
              <Plus size={16} /> Thêm mục
            </button>
            <p className="muted" style={{ fontSize: '0.78rem' }}>Nhớ bấm "Lưu cài đặt" ở trên sau khi chỉnh.</p>
          </div>
        </div>

        {/* Dịch vụ tư vấn */}
        <div className="card">
          <div className="card-head">
            <h3><ListChecks size={18} style={{ color: '#2563eb' }} /> Dịch vụ quan tâm (popup tư vấn)</h3>
          </div>
          <div className="card-pad stack" style={{ gap: 10 }}>
            <p className="page-subtitle">Danh sách hiển thị trong dropdown "Dịch vụ quan tâm" của popup đăng ký tư vấn.</p>
            {config.consultationServices.map((svc, i) => (
              <div className="row" key={i}>
                <input
                  className="input"
                  value={svc}
                  placeholder="Tên dịch vụ"
                  onChange={(e) =>
                    setConfig({
                      ...config,
                      consultationServices: config.consultationServices.map((s, idx) => (idx === i ? e.target.value : s)),
                    })
                  }
                />
                <button
                  className="btn-icon danger"
                  onClick={() =>
                    setConfig({ ...config, consultationServices: config.consultationServices.filter((_, idx) => idx !== i) })
                  }
                  title="Xoá"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
            {config.consultationServices.length === 0 && (
              <p className="muted" style={{ fontStyle: 'italic' }}>Chưa có dịch vụ nào.</p>
            )}
            <button
              className="add-link"
              style={{ alignSelf: 'flex-start' }}
              onClick={() => setConfig({ ...config, consultationServices: [...config.consultationServices, ''] })}
            >
              <Plus size={16} /> Thêm dịch vụ
            </button>
            <p className="muted" style={{ fontSize: '0.78rem' }}>Nhớ bấm "Lưu cài đặt" ở trên sau khi chỉnh.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
